/*@jsxRuntime automatic @jsxImportSource react*/
// ID of the semaphore
// a descriptive title
// Enables or disables processing. When a delivery fails (see below), "suspend" choices set this switch to false to stop processing while keeping ingress on.
// can be considered the master switch of the semaphore
// Maximum value the semaphore can reach
// Maximum value scope. When set to "total" all channels in the semaphore comprise a single queue. When set to "channel" every channel can go up to maxValue and is queued separately
// default is "channel"
// semaphore owner [read only]
// Semaphore create time [read only]
// Semaphore update time [read only]
// HTTP Callback configuration
// request protocol
// url address of the callback endpoint
// enables or disables the https callback
// http method of the callback endpoint
// defines the behavior in case delivery fails. "suspendProcessing" will set .isActive to false. Possible values: "drop" will drop the message and continue processing.
// websockets options.
// When a message arrives via websockets, it is assumed that the response is sent to the same websocket session that initiated the request.
// This may be controlled by the routing response from the mapping (see below). We are looking at more ways to make this more flexible.
// enables or disables websockets usage
// allows websockets clients to restore session in case of reconnect
// defines the behavior in case the client connection dropped. Possible values: "redriveToHttps" will redrive to the callback, "drop" drops the message, "suspendProcessing" will set .isActive to false
// mapping options. Mappings are an advanced and experimental feature that allows a lot of freedom. It may reduce the number of helper calls
// or even replace the callback
// enables or disables mapping
// maximum execution time for the mapping callback handler in seconds
// enables or disables the ability of the callback handler to return alternative routing options
// The mapping handler code. See this link for details
// The mapping language. Currently only javascript
// Sample input data for the mapping. Can be used for reference and testing in WebSemaphore console
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = undefined;
import MonacoEditor from '@monaco-editor/react';
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    p: "p",
    code: "code",
    a: "a",
    h2: "h2",
    table: "table",
    thead: "thead",
    tr: "tr",
    th: "th",
    tbody: "tbody",
    td: "td",
    strong: "strong"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h1, {
      children: "Semaphore Configuration"
    }), "\n", _jsxs(_components.p, {
      children: ["The unified ", _jsx(_components.code, {
        children: "upsert"
      }), " method is used to update the semaphore configuration. When passed data with an ", _jsx(_components.code, {
        children: "id"
      }), " property,\nit will update or create a semaphore with this id. Otherwise a semaphore with a new id will be created.\nThe semaphore configuration object describes and sets the settings and is effective immediately for all events processed after an update."]
    }), "\n", _jsxs(_components.p, {
      children: ["Note that the options are equivalent to the individual semaphore settings\nin the ", _jsx(_components.a, {
        href: "https://www.websemaphore.com/semaphore",
        children: "semaphore settings"
      }), " section of the console."]
    }), "\n", _jsx(_components.h2, {
      children: "Semaphore Configuration Object Details"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Property"
          }), _jsx(_components.th, {
            children: "Description"
          }), _jsx(_components.th, {
            children: "Type"
          }), _jsx(_components.th, {
            children: "Possible Values"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "id"
          }), _jsx(_components.td, {
            children: "The ID of the semaphore."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "title"
          }), _jsx(_components.td, {
            children: "A descriptive title for the semaphore."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "isActive"
          }), _jsx(_components.td, {
            children: "Enables or disables processing."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "maxValue"
          }), _jsx(_components.td, {
            children: "The maximum value the semaphore can reach."
          }), _jsx(_components.td, {
            children: "number"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "maxValueScope"
          }), _jsx(_components.td, {
            children: "Maximum value scope."
          }), _jsx(_components.td, {
            children: "enum"
          }), _jsx(_components.td, {
            children: "- 'channel': Each channel can reach the maxValue individually."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
            children: "- 'total': The maxValue is applicable across all channels."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "owner"
          }), _jsx(_components.td, {
            children: "The owner of the semaphore (read-only)."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "created"
          }), _jsx(_components.td, {
            children: "The creation time of the semaphore (read-only)."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "updated"
          }), _jsx(_components.td, {
            children: "The update time of the semaphore (read-only)."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.strong, {
              children: "callback"
            })
          }), _jsx(_components.td, {
            children: "HTTP Callback configuration."
          }), _jsx(_components.td, {
            children: "object"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "callback.protocol"
          }), _jsx(_components.td, {
            children: "The request protocol for the callback endpoint."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "callback.address"
          }), _jsx(_components.td, {
            children: "The URL address of the callback endpoint."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "callback.isActive"
          }), _jsx(_components.td, {
            children: "Enables or disables the HTTP callback."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "callback.method"
          }), _jsx(_components.td, {
            children: "The HTTP method to use for the callback endpoint."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "callback.onDeliveryError"
          }), _jsx(_components.td, {
            children: "Defines the behavior in case of delivery error."
          }), _jsx(_components.td, {
            children: "enum"
          }), _jsx(_components.td, {
            children: "- 'suspendProcessing': Stop processing while keeping ingress on."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
            children: "- 'drop': Drop the message and continue processing."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.strong, {
              children: "websockets"
            })
          }), _jsx(_components.td, {
            children: "Websockets options."
          }), _jsx(_components.td, {
            children: "object"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "websockets.isActive"
          }), _jsx(_components.td, {
            children: "Enables or disables websockets usage."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "websockets.allowContinuedSessions"
          }), _jsx(_components.td, {
            children: "Allows websockets clients to restore session in case of reconnect."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "websockets.onClientDropped"
          }), _jsx(_components.td, {
            children: "Defines the behavior in case the client connection is dropped."
          }), _jsx(_components.td, {
            children: "enum"
          }), _jsx(_components.td, {
            children: "- 'redriveToHttps': Redrive the message to the HTTP callback."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
            children: "- 'drop': Drop the message."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {}), _jsx(_components.td, {
            children: "- 'suspendProcessing': Stop processing."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping"
          }), _jsx(_components.td, {
            children: "Mapping options."
          }), _jsx(_components.td, {
            children: "object"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.isActive"
          }), _jsx(_components.td, {
            children: "Enables or disables mapping."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.maxExecutionTime"
          }), _jsx(_components.td, {
            children: "Maximum execution time for the mapping callback handler in seconds."
          }), _jsx(_components.td, {
            children: "number"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.canOverrideRouting"
          }), _jsx(_components.td, {
            children: "Enables or disables the ability of the callback handler to return alternative routing options."
          }), _jsx(_components.td, {
            children: "boolean"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.handler"
          }), _jsx(_components.td, {
            children: "The mapping handler code."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.language"
          }), _jsx(_components.td, {
            children: "The mapping language."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "mapping.inputData"
          }), _jsx(_components.td, {
            children: "Sample input data for the mapping."
          }), _jsx(_components.td, {
            children: "string"
          }), _jsx(_components.td, {})]
        })]
      })]
    }), "\n", _jsx(_components.h2, {
      children: "Sample Semaphore Configuration"
    }), "\n", _jsx(MonacoEditor, {
      theme: "vs-dark",
      height: "50vh",
      language: "json",
      value: JSON.stringify({
        // ID of the semaphore
        "id": "c3RydWN0dXJlLTE2ODk1MzQ1NDM2OTg=",
        // a descriptive title
        "title": "structure",
        // Enables or disables processing. When a delivery fails (see below), "suspend" choices set this switch to false to stop processing while keeping ingress on.
        // can be considered the master switch of the semaphore
        "isActive": true,
        // Maximum value the semaphore can reach
        "maxValue": 10,
        // Maximum value scope. When set to "total" all channels in the semaphore comprise a single queue. When set to "channel" every channel can go up to maxValue and is queued separately
        // default is "channel"
        "maxValueScope": "channel",
        // semaphore owner [read only]
        "owner": "igor.rubinovich@broadmind.eu",
        // Semaphore create time [read only]
        "created": "2023-07-16T19:09:03.698Z",
        // Semaphore update time [read only]
        "updated": "2023-07-16T19:09:03.698Z",
        // HTTP Callback configuration
        "callback": {
          // request protocol
          "protocol": "https",
          // url address of the callback endpoint
          "address": "www.example.com/budget/notify",
          // enables or disables the https callback
          "isActive": true,
          // http method of the callback endpoint
          "method": "POST",
          // defines the behavior in case delivery fails. "suspendProcessing" will set .isActive to false. Possible values: "drop" will drop the message and continue processing.
          "onDeliveryError": "suspendProcessing"
        },
        // websockets options.
        // When a message arrives via websockets, it is assumed that the response is sent to the same websocket session that initiated the request.
        // This may be controlled by the routing response from the mapping (see below). We are looking at more ways to make this more flexible.
        "websockets": {
          // enables or disables websockets usage
          "isActive": true,
          // allows websockets clients to restore session in case of reconnect
          "allowContinuedSessions": true,
          // defines the behavior in case the client connection dropped. Possible values: "redriveToHttps" will redrive to the callback, "drop" drops the message, "suspendProcessing" will set .isActive to false
          "onClientDropped": "redriveToHttps"
        },
        // mapping options. Mappings are an advanced and experimental feature that allows a lot of freedom. It may reduce the number of helper calls
        // or even replace the callback
        "mapping": {
          // enables or disables mapping
          "isActive": true,
          // maximum execution time for the mapping callback handler in seconds
          "maxExecutionTime": 5,
          // enables or disables the ability of the callback handler to return alternative routing options
          "canOverrideRouting": true,
          // The mapping handler code. See this link for details
          "handler": "const handler = (message, context) => {\n    const body = message.body;\n    return {\n        institution: body.title,\n        budget: 100000 * body.engagement,\n        country: body.Country,\n        requestor: context.semaphore.owner\n    }\n}",
          // The mapping language. Currently only javascript
          "language": "javascript",
          // Sample input data for the mapping. Can be used for reference and testing in WebSemaphore console
          "inputData": "{\n\t\"body\": {\n\t\t\"title\": \"CERN\",\n\t\t\"Country\": \"CH\",\n\t\t\"engagement\": 0.2\n\t},\n\t\"isTest\": true,\n\t\"id\": \"1689410125236-4\"\n}"
        }
      }, null, "\t")
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
